import { useEffect, useState } from "react";
import axios from "axios";
import { HCLayout } from "@/components/hc/HCLayout";

const API = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ label, value, suffix = "" }) => (
    <div className="panel px-5 py-5">
        <p className="mono-label" style={{ fontSize: 10 }}>{label}</p>
        <p className="font-serif mt-2" style={{ fontSize: 34, lineHeight: 1 }}>
            {value}
            <span style={{ fontSize: 18, color: "var(--ink-muted)" }}>{suffix}</span>
        </p>
    </div>
);

export default function Learning() {
    const [data, setData] = useState(null);

    useEffect(() => {
        axios.get(`${API}/api/mltc/learning`).then((r) => setData(r.data));
    }, []);

    if (!data) {
        return (
            <HCLayout>
                <p className="mono-label">Loading learning view…</p>
            </HCLayout>
        );
    }

    const c = data.cohort;
    const inst = data.this_instance;

    return (
        <HCLayout>
            <section className="border-b hairline pb-8" data-testid="learning-hero">
                <p className="mono-label">Learning view · synthetic model-of-care metrics</p>
                <h1
                    className="font-serif mt-3 max-w-4xl"
                    style={{ fontSize: "clamp(28px, 4vw, 46px)", lineHeight: 1.1, letterSpacing: "-0.01em" }}
                >
                    Process evidence that refines the model of care — without
                    centralising the patient's information
                </h1>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }} data-testid="learning-label">
                    {data.label}
                </p>
            </section>

            <section className="mt-8" data-testid="freeze-reasons">
                <p className="mono-label">Where reviews freeze (synthetic cohort of {c.reviews_in_cohort} reviews)</p>
                <div className="mt-4 grid gap-4">
                    {c.freeze_reasons.map((f) => (
                        <div key={f.gate} className="panel px-5 py-4">
                            <div className="flex items-center justify-between gap-4">
                                <p className="text-sm" style={{ color: "var(--ink)" }}>{f.gate}</p>
                                <p className="mono-data">{f.pct}%</p>
                            </div>
                            <div className="mt-2 h-2 w-full" style={{ background: "var(--paper-alt)" }}>
                                <div style={{ width: `${f.pct}%`, height: "100%", background: "var(--forest)" }} />
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="mt-10" data-testid="cohort-metrics">
                <p className="mono-label">Coordination & accountability signals</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <Stat label="Median time to resolution" value={c.median_time_to_resolution_days} suffix=" days" />
                    <Stat label="Repeated requests per review" value={c.repeated_requests_avg} suffix=" avg" />
                    <Stat label="Boundary crossings per review" value={c.boundary_crossings_median} suffix=" median" />
                    <Stat label="Unresolved follow-up ownership" value={c.unresolved_ownership_pct} suffix="%" />
                    <Stat label="Service-capacity failures" value={c.service_capacity_failure_pct} suffix="%" />
                    <Stat label="Patient involvement respected" value={c.patient_involvement_respected_pct} suffix="%" />
                    <Stat label="Escalations" value={c.escalation_pct} suffix="%" />
                    <Stat label="Conditional releases" value={c.conditional_release_pct} suffix="%" />
                </div>
            </section>

            <section className="panel-alt mt-10 px-6 py-6" data-testid="instance-metrics">
                <p className="mono-label">This demonstrator instance (live counts)</p>
                <div className="mt-4 grid gap-x-10 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                        ["Journeys started", inst.journeys_started],
                        ["Receipts issued", inst.receipts_issued],
                        ["Capacity exceptions", inst.capacity_exceptions],
                        ["Decisions recorded", Object.entries(inst.decisions).map(([k, v]) => `${k}: ${v}`).join(" · ") || "none yet"],
                    ].map(([k, v]) => (
                        <div key={k}>
                            <p className="mono-label" style={{ fontSize: 10 }}>{k}</p>
                            <p className="font-serif mt-1" style={{ fontSize: 22 }}>{v}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-5 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                    Only process signals appear here — freeze reasons, timings, boundary crossings and
                    outcomes. The content of Elsie's record, her preferences and each role's evidence
                    remain with their custodians and never aggregate into a central store.
                </p>
            </section>
        </HCLayout>
    );
}
